/**
 * The look card (RC2-07) — a focused `@annwn/ui` `Popup` over what the cursor is
 * pointed at.
 *
 * Built to the same handoff contract as the action menu, and it has to be: this
 * is an overlay that opens while the player is steering a cursor with the
 * direction keys, so "who has the keyboard" is not a detail here, it is the
 * whole interaction.
 *
 * The one difference from the menu, and it is deliberate: **look mode keeps the
 * keyboard.** The card is not modal in the game's sense — arrowing to the next
 * tile should move the cursor and re-answer the card, not be swallowed by an
 * overlay. So `Game` leaves its input enabled while this is up, the card focuses
 * its own dismiss control (so Escape and Tab behave, and a screen reader lands
 * on it), and the card's verbs are reachable by mouse. Closing hands focus back
 * to the canvas the way `Popup` always does.
 *
 * It holds no game state: `Game` passes an `ExamineCard` built by the view model
 * and gets back a verb id or a dismiss.
 */

import { useEffect, useRef } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import { Badge, Menu, Popup } from '@annwn/ui';

import type { ExamineCard } from './examine-view-model.js';

export interface ExamineCardOptions {
  /** A verb on the card was chosen — `forage`, `target`, `camp`. */
  onVerb(id: string): void;
  /** The card was dismissed (Escape, the backdrop, or the close). */
  onClose(): void;
}

interface PanelProps extends ExamineCardOptions {
  readonly card: ExamineCard;
}

function ExamineCardPanel({ card, onVerb, onClose }: PanelProps) {
  const wrap = useRef<HTMLDivElement>(null);

  // Focus the card itself rather than its first verb: unlike the action menu,
  // this overlay does not take the keyboard from the game, and stealing the
  // selection would make the next arrow key pick a verb instead of moving the
  // look cursor one tile east.
  useEffect(() => {
    wrap.current?.focus();
  }, [card.x, card.y]);

  return (
    <Popup open title={card.title} onClose={onClose}>
      <div className="examine-card" ref={wrap} tabIndex={-1}>
        <div aria-hidden="true" className="examine-card__glyph">
          {card.glyph}
        </div>

        <div className="examine-card__prose">
          {card.lines.map((line, index) => (
            // The server composed these in reading order and they carry no ids;
            // the index is the only stable key there is, and the list is
            // replaced wholesale on every answer rather than reordered.
            <p key={`${card.x},${card.y}:${index}`}>{line}</p>
          ))}
        </div>

        {card.badge || card.verbs.length > 0 ? (
          <div className="examine-card__foot">
            {card.badge ? <Badge tone={card.badge.tone}>{card.badge.label}</Badge> : null}
            {card.verbs.length > 0 ? (
              <Menu
                className="examine-card__verbs"
                items={card.verbs}
                label={`What to do with ${card.title}`}
                onSelect={(item) => {
                  if (!item.disabled) onVerb(item.id);
                }}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </Popup>
  );
}

export interface ExamineCardHandle {
  /** Show a card, or replace the one showing. `null` closes. */
  show(card: ExamineCard | null): void;
  destroy(): void;
}

export function mountExamineCard(
  container: HTMLElement,
  options: ExamineCardOptions,
): ExamineCardHandle {
  const root: Root = createRoot(container);
  let card: ExamineCard | null = null;

  const render = (): void => {
    root.render(
      card ? (
        <ExamineCardPanel card={card} onClose={options.onClose} onVerb={options.onVerb} />
      ) : null,
    );
  };

  render();

  return {
    show(next) {
      card = next;
      render();
    },
    destroy() {
      root.unmount();
    },
  };
}
